import { useEffect, useMemo, useState } from 'react';
import http from 'hub-http/clients/apiClient';
import { RequestStatuses, useAuthInfoProvider } from 'content-management-data-lib';
import { requestStatusLoading } from '../utils/requestStatusUtils';
const fetchTeams = ({
  userHasTeamAssignmentScope,
  userHasAdvancedModelingAccess
}) => {
  if (!userHasTeamAssignmentScope) {
    return http.get('app-users/v1/teams/user/all', {
      query: {
        includeChildTeamMembers: true,
        includeHierarchy: true
      }
    });
  } else if (userHasAdvancedModelingAccess) {
    return http.get('app-users/v1/teams/hierarchy');
  }
  return Promise.resolve([]);
};
export const useFetchTeams = () => {
  const [teamsRequestStatus, setTeamsRequestStatus] = useState(RequestStatuses.UNINITIALIZED);
  const [teams, setTeams] = useState([]);
  const {
    userInfo: {
      gates,
      user: {
        scopes
      }
    }
  } = useAuthInfoProvider();
  const userHasTeamAssignmentScope = gates.includes('content-partitioning-teams-v2') && scopes.includes('teams-content-partitioning') && scopes.includes('content-team-assignment');
  const userHasAdvancedModelingAccess = scopes.includes('teams-advanced-modeling-access');
  useEffect(() => {
    if (teamsRequestStatus === RequestStatuses.UNINITIALIZED) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTeamsRequestStatus(RequestStatuses.PENDING);
      fetchTeams({
        userHasAdvancedModelingAccess,
        userHasTeamAssignmentScope
      }).then(teamsData => {
        setTeams(teamsData);
        setTeamsRequestStatus(RequestStatuses.SUCCEEDED);
      }).catch(() => {
        setTeamsRequestStatus(RequestStatuses.FAILED);
      });
    }
  }, [teamsRequestStatus, userHasAdvancedModelingAccess, userHasTeamAssignmentScope]);
  return {
    teams,
    userHasTeamAssignmentScope,
    teamsRequestStatus
  };
};
export const useFetchUsers = () => {
  const [usersRequestStatus, setUsersRequestStatus] = useState(RequestStatuses.UNINITIALIZED);
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (usersRequestStatus === RequestStatuses.UNINITIALIZED) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUsersRequestStatus(RequestStatuses.PENDING);
      http.get('users/v2/app/hub-users', {
        query: {
          active: true,
          visible: true
        }
      }).then(usersData => {
        setUsers(usersData);
        setUsersRequestStatus(RequestStatuses.SUCCEEDED);
      }).catch(() => {
        setUsersRequestStatus(RequestStatuses.FAILED);
      });
    }
  }, [usersRequestStatus]);
  return {
    users,
    usersRequestStatus
  };
};
export const useFetchUsersAndTeams = () => {
  const {
    teams,
    userHasTeamAssignmentScope,
    teamsRequestStatus
  } = useFetchTeams();
  const {
    users,
    usersRequestStatus
  } = useFetchUsers();
  const isAnyRequestLoading = requestStatusLoading(teamsRequestStatus) || requestStatusLoading(usersRequestStatus);
  const hasAnyRequestFailed = teamsRequestStatus === RequestStatuses.FAILED || usersRequestStatus === RequestStatuses.FAILED;
  const usersAndTeamsData = useMemo(() => {
    return {
      hasAnyRequestFailed,
      isAnyRequestLoading,
      teams,
      users,
      userHasTeamAssignmentScope
    };
  }, [teams, users, userHasTeamAssignmentScope, isAnyRequestLoading, hasAnyRequestFailed]);
  return usersAndTeamsData;
};